// "Cierres por periodo" in the Dashboard: how many closings (the Admin's "loops") a client
// had per week, month and year, by how they stand. The pure half.
//
// Nothing is stored per month or per year anywhere: the backend keeps closings per day. So this
// folds `getCierreClosingDates`' days, each counted by `resumenDeFecha` — the panel grouped by date's
// own rule — so a period's counts are the sum of the lines the Panel shows for its days.
//
// Dates are calendar days, never instants: every one is built with `Date.UTC` from its three numbers
// and read back with the UTC getters, because `new Date("2026-09-01")` is 31 August in Bogotá.

import { resumenDeFecha } from "./cierrePanelFechas";

export const MODO = { SEMANA: "semana", MES: "mes", ANIO: "anio" };

// How many periods each grouping draws. A year is drawn from the first year with closings.
const CUANTOS = { semana: 12, mes: 12 };

// Written out rather than asked of `toLocaleDateString`, as `cierreSabados.js` does.
const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

export const ESTADOS_VOLUMEN = [
  { clave: "ok", etiqueta: "Cuadre OK" },
  { clave: "alerta", etiqueta: "Con alerta" },
  { clave: "sinReportes", etiqueta: "Sin reportes" },
  { clave: "porRevisar", etiqueta: "Por revisar" },
];

const partes = (fecha) => String(fecha).slice(0, 10).split("-").map(Number);
const utc = (fecha) => {
  const [y, m, d] = partes(fecha);
  return new Date(Date.UTC(y, m - 1, d));
};
const iso = (d) => d.toISOString().slice(0, 10);
const dosDigitos = (n) => String(n).padStart(2, "0");
const diasDelMes = (anio, mes) => new Date(Date.UTC(anio, mes, 0)).getUTCDate();

function sumarDias(fecha, dias) {
  const d = utc(fecha);
  d.setUTCDate(d.getUTCDate() + dias);
  return iso(d);
}

// The Monday (ISO week) a day belongs to.
export function lunesDe(fecha) {
  const d = utc(fecha);
  return sumarDias(fecha, -((d.getUTCDay() + 6) % 7));
}

// The period a day falls in: its week's Monday, `YYYY-MM` or `YYYY`.
export function claveDePeriodo(fecha, modo) {
  if (modo === MODO.SEMANA) return lunesDe(fecha);
  if (modo === MODO.MES) return String(fecha).slice(0, 7);
  return String(fecha).slice(0, 4);
}

// The period before one, by its key.
function periodoAnterior(clave, modo) {
  if (modo === MODO.SEMANA) return sumarDias(clave, -7);
  if (modo === MODO.MES) {
    const [y, m] = clave.split("-").map(Number);
    return m === 1 ? `${y - 1}-12` : `${y}-${dosDigitos(m - 1)}`;
  }
  return String(Number(clave) - 1);
}

// B-138 — the days a bar stands for, for the Dashboard's period: a week Monday to Sunday, a month
// from its 1st to its last day, a year 1 January to 31 December.
export function rangoDePeriodo(clave, modo) {
  if (modo === MODO.SEMANA) return { desde: clave, hasta: sumarDias(clave, 6) };
  if (modo === MODO.MES) {
    const [y, m] = clave.split("-").map(Number);
    return { desde: `${clave}-01`, hasta: `${clave}-${dosDigitos(diasDelMes(y, m))}` };
  }
  return { desde: `${clave}-01-01`, hasta: `${clave}-12-31` };
}

// "13-19 oct", "29 dic-4 ene"; "oct 2026"; "2026".
export function etiquetaDePeriodo(clave, modo) {
  if (modo === MODO.SEMANA) {
    const inicio = utc(clave);
    const fin = utc(sumarDias(clave, 6));
    const mesInicio = MESES[inicio.getUTCMonth()];
    const mesFin = MESES[fin.getUTCMonth()];
    return mesInicio === mesFin
      ? `${inicio.getUTCDate()}-${fin.getUTCDate()} ${mesFin}`
      : `${inicio.getUTCDate()} ${mesInicio}-${fin.getUTCDate()} ${mesFin}`;
  }
  if (modo === MODO.MES) {
    const [y, m] = clave.split("-").map(Number);
    return `${MESES[m - 1]} ${y}`;
  }
  return clave;
}

// The last periods up to the one `hoy` is in, oldest first, each with its closings by state. A period
// with no closings is there with zeros, so the axis does not skip it. Days after `hoy` are left out.
export function volumenPorPeriodo(dias, byState, modo, { hoy, cuantos } = {}) {
  const lineas = (dias || []).filter((dia) => dia?.businessDate && (!hoy || dia.businessDate <= hoy)).map((dia) => resumenDeFecha(dia, byState));
  const ultimo = claveDePeriodo(hoy || lineas.map((l) => l.fecha).sort().pop() || "1970-01-01", modo);

  let claves;
  if (modo === MODO.ANIO) {
    const primero = lineas.reduce((min, l) => (l.fecha.slice(0, 4) < min ? l.fecha.slice(0, 4) : min), ultimo);
    claves = [];
    for (let y = Number(primero); y <= Number(ultimo); y += 1) claves.push(String(y));
  } else {
    claves = [ultimo];
    const n = cuantos || CUANTOS[modo];
    while (claves.length < n) claves.unshift(periodoAnterior(claves[0], modo));
  }

  const vacios = new Map(claves.map((clave) => [clave, { clave, etiqueta: etiquetaDePeriodo(clave, modo), total: 0, ok: 0, alerta: 0, sinReportes: 0, porRevisar: 0 }]));
  lineas.forEach((linea) => {
    const periodo = vacios.get(claveDePeriodo(linea.fecha, modo));
    if (!periodo) return;
    periodo.total += linea.total;
    ESTADOS_VOLUMEN.forEach(({ clave }) => {
      periodo[clave] += linea[clave];
    });
  });
  return [...vacios.values()];
}

// The same day of the period before, kept inside that period: 31 March against 28 (or 29) February,
// 29 February against 28 February.
function mismoDiaDelPeriodoAnterior(hoy, modo) {
  if (modo === MODO.SEMANA) return sumarDias(hoy, -7);
  const [y, m, d] = partes(hoy);
  if (modo === MODO.MES) {
    const anio = m === 1 ? y - 1 : y;
    const mes = m === 1 ? 12 : m - 1;
    return `${anio}-${dosDigitos(mes)}-${dosDigitos(Math.min(d, diasDelMes(anio, mes)))}`;
  }
  return `${y - 1}-${dosDigitos(m)}-${dosDigitos(Math.min(d, diasDelMes(y - 1, m)))}`;
}

const inicioDe = (fecha, modo) => {
  if (modo === MODO.SEMANA) return lunesDe(fecha);
  if (modo === MODO.MES) return `${String(fecha).slice(0, 7)}-01`;
  return `${String(fecha).slice(0, 4)}-01-01`;
};

const totalEntre = (dias, desde, hasta) =>
  (dias || []).reduce((suma, dia) => (dia?.businessDate >= desde && dia.businessDate <= hasta ? suma + (dia.closings || 0) : suma), 0);

// This week, this month and this year so far, each against the same stretch of the period before
// (Monday to the same weekday, the 1st to the same day, 1 January to the same date), so a period
// still running is not compared with a whole one. `variacion` is a whole percentage, or null when
// the period before had no closings: there is nothing to compare with.
export function kpisDeVolumen(dias, hoy) {
  return [MODO.SEMANA, MODO.MES, MODO.ANIO].map((modo) => {
    const actual = totalEntre(dias, inicioDe(hoy, modo), hoy);
    const finAnterior = mismoDiaDelPeriodoAnterior(hoy, modo);
    const anterior = totalEntre(dias, inicioDe(finAnterior, modo), finAnterior);
    return {
      modo,
      actual,
      anterior,
      variacion: anterior > 0 ? Math.round(((actual - anterior) / anterior) * 100) : null,
    };
  });
}
